import { z } from "zod";

import { activitySessionSchema, scheduleDateSchema } from "./scheduling.js";

export const calendarQuerySchema = z.object({
  startDate: scheduleDateSchema.optional(),
});

export const calendarSessionSchema = activitySessionSchema.extend({
  activityTitle: z.string().min(1),
  activityDurationMinutes: z.number().int().positive(),
  completedSessionCount: z.number().int().nonnegative(),
  hasFlashcards: z.boolean(),
});

export const calendarDaySchema = z.object({
  date: scheduleDateSchema,
  orderVersion: z.number().int().positive(),
  totalActivityDurationMinutes: z.number().int().nonnegative(),
  sessions: z.array(calendarSessionSchema),
});

export const calendarResponseSchema = z.object({
  range: z.object({
    startDate: scheduleDateSchema,
    endDate: scheduleDateSchema,
    currentDate: scheduleDateSchema,
    timeZone: z.string(),
    usedFallbackTimeZone: z.boolean(),
  }),
  metrics: z.object({
    sessionCount: z.number().int().nonnegative(),
    completedSessionCount: z.number().int().nonnegative(),
    totalActivityDurationMinutes: z.number().int().nonnegative(),
  }),
  days: z.array(calendarDaySchema).length(7),
});

export const moveCalendarSessionInputSchema = z
  .object({
    version: z.number().int().positive(),
    scheduledDate: scheduleDateSchema,
    idempotencyKey: z.uuid(),
  })
  .strict();

export const moveCalendarSessionResponseSchema = z.object({
  session: calendarSessionSchema,
  moved: z.boolean(),
  replayed: z.boolean(),
});

export const reorderCalendarDayInputSchema = z
  .object({
    version: z.number().int().positive(),
    sessionIds: z.array(z.uuid()).min(1),
    idempotencyKey: z.uuid(),
  })
  .strict()
  .refine((input) => new Set(input.sessionIds).size === input.sessionIds.length, {
    path: ["sessionIds"],
    message: "Session identifiers must be unique.",
  });

export const reorderCalendarDayResponseSchema = z.object({
  scheduledDate: scheduleDateSchema,
  sessionIds: z.array(z.uuid()).min(1),
  orderVersion: z.number().int().positive(),
  reordered: z.boolean(),
  replayed: z.boolean(),
});

export const duplicateCalendarSessionInputSchema = z
  .object({
    version: z.number().int().positive(),
    idempotencyKey: z.uuid(),
  })
  .strict();

export const duplicateCalendarSessionResponseSchema = z.object({
  session: calendarSessionSchema,
  replayed: z.boolean(),
});

export type CalendarQuery = z.infer<typeof calendarQuerySchema>;
export type CalendarSession = z.infer<typeof calendarSessionSchema>;
export type CalendarDay = z.infer<typeof calendarDaySchema>;
export type CalendarResponse = z.infer<typeof calendarResponseSchema>;
export type MoveCalendarSessionInput = z.infer<typeof moveCalendarSessionInputSchema>;
export type MoveCalendarSessionResponse = z.infer<typeof moveCalendarSessionResponseSchema>;
export type ReorderCalendarDayInput = z.infer<typeof reorderCalendarDayInputSchema>;
export type ReorderCalendarDayResponse = z.infer<typeof reorderCalendarDayResponseSchema>;
export type DuplicateCalendarSessionInput = z.infer<typeof duplicateCalendarSessionInputSchema>;
export type DuplicateCalendarSessionResponse = z.infer<
  typeof duplicateCalendarSessionResponseSchema
>;
